import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { listingsApi, getFileUrl, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getCrownVicImage } from '../lib/images';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, DollarSign, MapPin, Calendar, Gauge, Mail, Phone, User, ChevronLeft, ChevronRight, Upload, Trash2 } from 'lucide-react';

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await listingsApi.getOne(id);
        setListing(response.data);
      } catch (error) {
        console.error('Error fetching listing:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
  }, [id]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  const formatMileage = (mileage) => {
    if (!mileage) return 'N/A';
    return new Intl.NumberFormat('en-US').format(mileage) + ' miles';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? (listing.images?.length || 1) - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === (listing.images?.length || 1) - 1 ? 0 : prev + 1));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const existingCount = listing.images?.length || 0;
    const available = 10 - existingCount;
    if (available <= 0) {
      alert('This listing already has the maximum of 10 photos. Remove one first.');
      return;
    }
    const toUpload = files.slice(0, available);
    if (files.length > available) {
      alert(`Only ${available} more photo${available === 1 ? '' : 's'} can be added (10 max). Uploading ${toUpload.length}.`);
    }

    setUploading(true);
    try {
      for (const f of toUpload) {
        try {
          await listingsApi.uploadImage(id, f);
        } catch (imgErr) {
          console.error('Upload failed for', f.name, imgErr);
        }
      }
      const response = await listingsApi.getOne(id);
      setListing(response.data);
    } catch (error) {
      alert(formatApiError(error));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (imageIndex) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await listingsApi.deleteImage(id, imageIndex);
      const response = await listingsApi.getOne(id);
      setListing(response.data);
      setCurrentImageIndex(0);
    } catch (error) {
      alert(formatApiError(error));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await listingsApi.delete(id);
      navigate('/marketplace');
    } catch (error) {
      alert(formatApiError(error));
    }
  };

  const isOwner = user && listing && user.id === listing.seller_id;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-400 tag">Loading listing...</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-4">Listing not found</p>
          <Link to="/marketplace">
            <Button className="bg-[#FF3B30] hover:bg-[#E6352B]">Back to Marketplace</Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = listing.images?.length > 0 
    ? listing.images.map(img => getFileUrl(img))
    : [getCrownVicImage(listing.id)];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Back Button */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-4">
          <Link to="/marketplace" className="inline-flex items-center text-gray-400 hover:text-white" data-testid="back-to-marketplace-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Marketplace
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Images */}
          <div className="lg:col-span-2">
            {/* Main Image */}
            <div className="relative aspect-video bg-[#141414] rounded-lg overflow-hidden mb-4">
              <img
                src={images[currentImageIndex]}
                alt={listing.title}
                className="w-full h-full object-cover"
              />
              
              {images.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-[#FF3B30]' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </>
              )}

              <span className="absolute top-4 right-4 tag bg-[#FF3B30] text-white px-3 py-1.5 rounded">
                For Sale
              </span>
            </div>

            {/* Thumbnail Strip */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-16 rounded overflow-hidden border-2 transition-colors ${
                      idx === currentImageIndex ? 'border-[#FF3B30]' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Upload Image (Owner Only) */}
            {isOwner && (
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || (listing.images?.length || 0) >= 10}
                  variant="outline"
                  className="border-[#262626] text-white"
                  data-testid="upload-image-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Add Photos'}
                </Button>
                <span className="text-gray-500 text-xs">
                  {listing.images?.length || 0}/10 photos
                </span>
                {(listing.images?.length || 0) > 0 && (
                  <Button
                    onClick={() => handleDeleteImage(currentImageIndex)}
                    variant="ghost"
                    className="text-red-400 hover:text-red-300"
                    data-testid="delete-current-image-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Current Photo
                  </Button>
                )}
              </div>
            )}

            {/* Description */}
            <div className="mt-8">
              <h2 className="font-heading font-bold text-xl text-white mb-4">Description</h2>
              <p className="text-gray-300 whitespace-pre-wrap">{listing.description}</p>
            </div>

            {/* Features */}
            {listing.features?.length > 0 && (
              <div className="mt-8">
                <h2 className="font-heading font-bold text-xl text-white mb-4">Features</h2>
                <div className="flex flex-wrap gap-2">
                  {listing.features.map((feature, idx) => (
                    <span key={idx} className="bg-[#262626] text-gray-300 px-3 py-1.5 rounded text-sm">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Details & Contact */}
          <div className="space-y-6">
            {/* Price Card */}
            <Card className="bg-[#141414] border-[#262626] p-6">
              <h1 className="font-heading font-bold text-2xl text-white mb-4">{listing.title}</h1>
              
              <div className="text-[#FF3B30] font-bold text-4xl flex items-center mb-6">
                <DollarSign className="w-8 h-8" />
                {formatPrice(listing.price).replace('$', '')}
              </div>

              <div className="space-y-3 text-gray-300">
                <p className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-500" />
                  <span className="font-medium">{listing.year}</span> Crown Victoria
                </p>
                <p className="flex items-center gap-3">
                  <Gauge className="w-5 h-5 text-gray-500" />
                  {formatMileage(listing.mileage)}
                </p>
                <p className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-500" />
                  {listing.location}
                </p>
              </div>
            </Card>

            {/* Contact Card */}
            <Card className="bg-[#141414] border-[#262626] p-6">
              <h3 className="font-heading font-bold text-lg text-white mb-4">Contact Seller</h3>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#262626] flex items-center justify-center text-white font-bold">
                  {listing.seller_name?.[0]?.toUpperCase() || 'S'}
                </div>
                <div>
                  <p className="font-medium text-white">{listing.seller_name}</p>
                  <p className="text-xs text-gray-500">Listed {formatDate(listing.created_at)}</p>
                </div>
              </div>

              <div className="space-y-3">
                <a 
                  href={`mailto:${listing.contact_email}`}
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
                  data-testid="contact-email-link"
                >
                  <Mail className="w-5 h-5 text-[#FF3B30]" />
                  {listing.contact_email}
                </a>
                {listing.contact_phone && (
                  <a 
                    href={`tel:${listing.contact_phone}`}
                    className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
                    data-testid="contact-phone-link"
                  >
                    <Phone className="w-5 h-5 text-[#FF3B30]" />
                    {listing.contact_phone}
                  </a>
                )}
              </div>

              <a href={`mailto:${listing.contact_email}?subject=Inquiry about ${listing.title}`}>
                <Button className="w-full mt-6 bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="email-seller-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Seller
                </Button>
              </a>
            </Card>

            {/* Owner Actions */}
            {isOwner && (
              <Card className="bg-[#141414] border-[#262626] p-6">
                <h3 className="font-heading font-bold text-lg text-white mb-4">Manage Listing</h3>
                <div className="space-y-3">
                  <Link to={`/marketplace/${id}/edit`}>
                    <Button variant="outline" className="w-full border-[#262626] text-white" data-testid="edit-listing-btn">
                      Edit Listing
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    onClick={handleDelete}
                    className="w-full border-red-500/50 text-red-500 hover:bg-red-500/10"
                    data-testid="delete-listing-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Listing
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
